import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import posthog from 'posthog-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getAnnounceDetails } from 'services/announce'
import { faFull, faPlain, longJalali } from 'utilities/helper/format'

const PAGE_SIZE = 5

// Wording copied from Android so the two clients read identically
// (MoneypoolAnnounceReceiverAdapter.bindTo). Raw values come from the announce
// microservice (Sms.Status / BaleMessage.Status).
type Tone = 'muted' | 'ok' | 'bad'
const TONE: { [k in Tone]: string } = {
    muted: 'text-text-secondary',
    ok: 'text-green-1',
    bad: 'text-red-500',
}
const SMS_STATUS: { [k: string]: { label: string; tone: Tone } } = {
    IN_PROGRESS: { label: 'در حال ارسال', tone: 'muted' },
    TO_MOKHABERAT: { label: 'در مخابرات', tone: 'muted' },
    FAILED: { label: 'ارسال ناموفق', tone: 'bad' },
    UNDELIVERED: { label: 'ارسال ناموفق', tone: 'bad' },
    DELIVERED: { label: 'ارسال موفق', tone: 'ok' },
    LAGHV: { label: 'لغو شده', tone: 'bad' },
    BLOCKED: { label: 'مسدود شده', tone: 'bad' },
}
// Bale has been down since 2026-06 and the announce service no longer books a
// Bale leg for new announcements; these rows still appear on the old ones that
// really went out through it.
const BALE_STATUS: { [k: string]: { label: string; tone: Tone } } = {
    pending: { label: 'در انتظار ارسال', tone: 'muted' },
    delivered: { label: 'ارسال موفق', tone: 'ok' },
    unsuccessful: { label: 'ارسال ناموفق', tone: 'bad' },
}

// Delivery, as the two status vocabularies spell it. Anything else — queued,
// «در مخابرات» — is neither yet, and counting it as either would make a
// still-sending announcement look finished.
const DELIVERED = new Set(['DELIVERED', 'delivered'])
const FAILED = new Set([
    'FAILED',
    'UNDELIVERED',
    'LAGHV',
    'BLOCKED',
    'unsuccessful',
])

/** Messages, not people: one receiver can be sent both an SMS and a Bale message. */
const countStatuses = (receivers: IAnnounceReceiver[]) => {
    let delivered = 0
    let failed = 0
    receivers.forEach((r) => {
        ;[r.sms_message, r.bale_message].forEach((message) => {
            if (!message) return
            if (DELIVERED.has(message.status)) delivered += 1
            else if (FAILED.has(message.status)) failed += 1
        })
    })
    return { delivered, failed }
}

// The API reports cost in rial; Android shows تومان (cost / 10) and «رایگان»
// for zero rather than a bare 0.
function toman(rial: number | undefined | null) {
    if (!rial) return 'رایگان'
    return `${faFull.format(Math.round(rial / 10))} تومان`
}

const faDigits = (s: string) =>
    s.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])

/**
 * «۲۶ شهریور ۱۴۰۵، ساعت ۱۳:۴۲» from the service's «1405-06-26 13:42:42».
 *
 * The raw string used to be printed as it came. Read right-to-left the
 * bidi algorithm put the clock before the date, and the clock itself was UTC —
 * fixed at the source, in the announce service, which now localises before
 * converting to Jalali.
 */
function whenLabel(createdAt?: string | null) {
    if (!createdAt) return '—'
    const [date, time] = createdAt.trim().split(/\s+/)
    const day = longJalali(date)
    if (!day) return faDigits(createdAt)
    const clock = time ? time.slice(0, 5) : ''
    return clock ? `${day}، ساعت ${faDigits(clock)}` : day
}

function ChannelRow({
    label,
    status,
    map,
    cost,
}: {
    label: string
    status: string
    map: { [k: string]: { label: string; tone: Tone } }
    cost: number
}) {
    const s = map[status]
    return (
        <div className="flex items-baseline justify-between gap-3 py-1">
            <span className="text-[13px] text-text-secondary shrink-0">
                {label}
            </span>
            <span
                className={`grow text-[13px] ${
                    s ? TONE[s.tone] : TONE.muted
                }`}
            >
                {s ? s.label : status}
            </span>
            <span className="text-[13px] text-text-body shrink-0">
                {toman(cost)}
            </span>
        </div>
    )
}

function ReceiverCard({ r }: { r: IAnnounceReceiver }) {
    const hasChannel = !!r.sms_message || !!r.bale_message
    return (
        <div className="mb-2 rounded-card border border-divider bg-white p-4 shadow-card">
            <div className="flex items-baseline justify-between gap-3">
                <p className="text-sm font-bold text-heading truncate">
                    {r.name || r.phone_number}
                </p>
                {r.name && r.phone_number && (
                    <p
                        dir="ltr"
                        className="shrink-0 text-xs text-text-secondary"
                    >
                        {faDigits(r.phone_number)}
                    </p>
                )}
            </div>
            <div className="mt-2 border-t border-divider pt-1">
                {r.sms_message && (
                    <ChannelRow
                        label="پیامک"
                        status={r.sms_message.status}
                        map={SMS_STATUS}
                        cost={r.sms_message.cost}
                    />
                )}
                {r.bale_message && (
                    <ChannelRow
                        label="بله"
                        status={r.bale_message.status}
                        map={BALE_STATUS}
                        cost={r.bale_message.cost}
                    />
                )}
                {!hasChannel && (
                    <p className="py-1 text-[13px] text-text-secondary">
                        پیامی برای این شماره ثبت نشده است
                    </p>
                )}
            </div>
        </div>
    )
}

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-baseline justify-between gap-3 py-1.5">
            <span className="text-sm text-text-secondary shrink-0">
                {label}
            </span>
            <span className="text-sm text-text-body text-left">{value}</span>
        </div>
    )
}

// Everything Android shows above the recipient list: the message text, the
// category, which channels carried it, when, and what it cost us.
function AnnounceSummary({ a }: { a: IAnnounceItem }) {
    const channels = a.channels?.map((c) => c.title).join('، ') || '—'
    // Only announcements that really went out through Bale carry a Bale cost;
    // for everything else the row would read «رایگان» under a channel that
    // was never used.
    const showBale = (a.total_bale_cost ?? 0) > 0
    return (
        <div className="mb-5">
            <p className="mb-2 text-sm font-bold text-heading">متن پیام</p>
            <p
                dir="auto"
                className="mb-4 whitespace-pre-line rounded-card border border-dashed border-divider bg-white p-3 text-sm leading-7 text-text-body"
            >
                {a.text}
            </p>
            <div className="rounded-card border border-divider bg-white px-4 py-2 shadow-card">
                <div className="flex items-center justify-between gap-3 py-1.5">
                    <span className="text-sm text-text-secondary shrink-0">
                        عنوان
                    </span>
                    {a.tags ? (
                        <span className="rounded-lg bg-[#E8F5EC] px-3 py-1 text-xs text-green-1 truncate">
                            {a.tags}
                        </span>
                    ) : (
                        <span className="text-sm text-text-body">—</span>
                    )}
                </div>
                <InfoRow label="ارسال از طریق" value={channels} />
                <InfoRow label="تاریخ ارسال" value={whenLabel(a.created_at)} />
                {a.receivers_count > 0 && (
                    <InfoRow
                        label="تعداد گیرندگان"
                        value={`${faPlain.format(a.receivers_count)} نفر`}
                    />
                )}
                <InfoRow label="هزینه پیامک" value={toman(a.total_sms_cost)} />
                {showBale && (
                    <InfoRow label="هزینه بله" value={toman(a.total_bale_cost)} />
                )}
            </div>
        </div>
    )
}

export default function AnnounceDetail() {
    const { id, announceId } = useParams()
    const [summary, setSummary] = useState<IAnnounceItem | null>(null)
    const [items, setItems] = useState<IAnnounceReceiver[]>([])
    const [page, setPage] = useState<number>(1)
    const [hasMore, setHasMore] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [loadingMore, setLoadingMore] = useState<boolean>(false)
    const reported = useRef<boolean>(false)

    const loadPage = useCallback(
        async (pageNumber: number, reset: boolean) => {
            if (!id || !announceId) return
            reset ? setLoading(true) : setLoadingMore(true)
            try {
                const res = await getAnnounceDetails(id, announceId, pageNumber)
                const batch = res?.announce_messages || []
                // keep the first header we got so paging never blanks it out
                if (res?.announce) setSummary(res.announce)
                setItems((prev) => (reset ? batch : [...prev, ...batch]))
                setHasMore(batch.length >= PAGE_SIZE)

                // Once, when the summary lands. What this announcement cost is
                // on the header and therefore exact; the delivery counts are
                // over the receivers loaded so far — the first page — so read
                // them against `receivers_count`, never as the total.
                //
                // No `phone_number` and no `name`: the recipient list is a
                // fund's membership roll and has no business leaving the device.
                if (reset && res?.announce && !reported.current) {
                    reported.current = true
                    const { delivered, failed } = countStatuses(batch)
                    posthog.capture('announce_detail_viewed', {
                        moneypool_id: Number(id),
                        announce_id: Number(announceId),
                        receivers_count: res.announce.receivers_count,
                        sms_cost_rial: res.announce.total_sms_cost,
                        bale_cost_rial: res.announce.total_bale_cost,
                        delivered_count: delivered,
                        failed_count: failed,
                    })
                }
            } catch {
                if (reset) setItems([])
                setHasMore(false)
            } finally {
                setLoading(false)
                setLoadingMore(false)
            }
        },
        [id, announceId]
    )

    useEffect(() => {
        loadPage(1, true)
    }, [loadPage])

    const onLoadMore = () => {
        const next = page + 1
        setPage(next)
        loadPage(next, false)
    }

    return (
        <div className="flex h-full flex-col bg-main-bg">
            <Header title="جزئیات اطلاع‌رسانی" />
            <div className="grow overflow-y-auto px-4 pt-4 pb-8">
                {loading ? (
                    <DotsLoading />
                ) : (
                    <>
                        {summary && <AnnounceSummary a={summary} />}
                        <p className="mb-2 text-sm font-bold text-heading">
                            دریافت‌کنندگان
                            {summary && summary.receivers_count > 0 && (
                                <span className="mr-1 text-xs font-normal text-text-secondary">
                                    ({faPlain.format(summary.receivers_count)}{' '}
                                    نفر)
                                </span>
                            )}
                        </p>
                        {items.length === 0 ? (
                            <div className="rounded-card border border-divider bg-white px-4 py-10 text-center text-sm text-text-secondary">
                                گیرنده‌ای برای این اطلاع‌رسانی یافت نشد
                            </div>
                        ) : (
                            <>
                                {items.map((r, i) => (
                                    <ReceiverCard
                                        key={`${r.phone_number}-${i}`}
                                        r={r}
                                    />
                                ))}
                                {hasMore && (
                                    <button
                                        onClick={onLoadMore}
                                        disabled={loadingMore}
                                        className="my-3 w-full rounded-full border border-green-1 py-2.5 text-sm text-green-1 disabled:opacity-60"
                                    >
                                        {loadingMore
                                            ? 'در حال بارگذاری…'
                                            : 'موارد بیشتر'}
                                    </button>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
